import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import saleService from '../services/saleService';
import platformService from '../services/platformService';
import { toast } from 'react-hot-toast';
import { getCountryName } from '../utils/countries';

const createDefaultFilters = () => ({
  search: '',
  platform: '',
  status: '',
  productCountry: '',
  productCity: '',
  customerId: '',
  customerIdValue: '',
  hasConversion: '',
  article: '',
  dateRange: {
    type: 'all',
    startDate: '',
    endDate: ''
  },
  executionDateRange: {
    type: 'all',
    startDate: '',
    endDate: ''
  }
});

const SalesScreen = () => {
  const location = useLocation();
  const [sales, setSales] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Estado para los filtros activos (los que se envían a la API)
  const [activeFilters, setActiveFilters] = useState(createDefaultFilters);

  // Estado para los filtros del formulario
  const [filters, setFilters] = useState(createDefaultFilters);
  const [isUrlFiltersInitialized, setIsUrlFiltersInitialized] = useState(false);
  
  // Paginación
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    pages: 0,
    from: 0,
    to: 0
  });
  
  // Ordenamiento
  const [sorting, setSorting] = useState({
    field: 'saleDate',
    order: 'DESC'
  });
  
  const [deleteDialog, setDeleteDialog] = useState({ 
    open: false, 
    sale: null,
    hasConversion: false 
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState('');
  const [selectedSales, setSelectedSales] = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkStatusDialog, setBulkStatusDialog] = useState({ open: false, status: '' });
  const [selectAllPages, setSelectAllPages] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  
  // Estado para el trabajo de carga
  const [uploadJob, setUploadJob] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isPolling, setIsPolling] = useState(false);

  const [locationFilters, setLocationFilters] = useState({});
  const [availableCities, setAvailableCities] = useState([]);

  // Calcular los botones de paginación a mostrar
  const paginationButtons = useMemo(() => {
    const buttons = [];
    if (pagination.pages <= 5) {
      // Si hay 5 o menos páginas, mostrar todas
      for (let i = 1; i <= pagination.pages; i++) {
        buttons.push(i);
      }
    } else {
      // Si hay más de 5 páginas, decidir qué mostrar
      if (pagination.page <= 3) {
        // Si estamos en las primeras páginas
        for (let i = 1; i <= 5; i++) {
          buttons.push(i);
        }
      } else if (pagination.page >= pagination.pages - 2) {
        // Si estamos en las últimas páginas
        for (let i = pagination.pages - 4; i <= pagination.pages; i++) {
          buttons.push(i);
        }
      } else {
        // Si estamos en páginas intermedias
        for (let i = pagination.page - 2; i <= pagination.page + 2; i++) {
          buttons.push(i);
        }
      }
    }
    return buttons;
  }, [pagination.page, pagination.pages]);

  const loadPlatforms = async () => {
    try {
      const data = await platformService.getActivePlatforms();
      setPlatforms(data || []);
    } catch (err) {
      console.error('Error al cargar plataformas:', err);
    }
  };

  useEffect(() => {
    loadPlatforms();
  }, []);

  // Cargar filtros de ubicación al montar el componente
  useEffect(() => {
    const fetchLocationFilters = async () => {
      try {
        const filters = await saleService.getLocationFilters();
        setLocationFilters(filters);
      } catch (error) {
        console.error('Error al cargar filtros de ubicación:', error);
      }
    };
    fetchLocationFilters();
  }, []);

  // Manejar cambio de país
  const handleCountryChange = (e) => {
    const selectedCountry = e.target.value;
    console.log('País seleccionado:', selectedCountry);
    console.log('Filtros de ubicación disponibles:', locationFilters);
    
    // Actualizar las ciudades disponibles para el país seleccionado
    if (selectedCountry && selectedCountry !== 'none' && locationFilters[selectedCountry]) {
      setAvailableCities(locationFilters[selectedCountry]);
      console.log('Ciudades disponibles para', selectedCountry, ':', locationFilters[selectedCountry]);
    } else {
      setAvailableCities([]);
      console.log('No hay ciudades disponibles para este país o se seleccionó "No definido"');
    }
    
    setFilters(prev => ({
      ...prev,
      productCountry: selectedCountry,
      productCity: '' // Resetear ciudad cuando cambia el país
    }));
  };

  // Función para manejar cambios en los filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('dateRange.') || name.includes('executionDateRange.')) {
      const [prefix, field] = name.split('.');
      setFilters(prev => ({
        ...prev,
        [prefix]: {
          ...prev[prefix],
          [field]: value
        }
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Función para aplicar los filtros
  const applyFilters = () => {
    setActiveFilters(filters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const loadSales = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Preparar filtros para la API, incluyendo paginación y ordenamiento
      const apiFilters = {
        page: pagination.page,
        limit: pagination.limit,
        sortField: sorting.field,
        sortOrder: sorting.order,
        include: ['GoogleAdsConversion']
      };

      // Solo añadir filtros si tienen valor
      if (activeFilters.platform) apiFilters.platform = activeFilters.platform;
      if (activeFilters.status) apiFilters.status = activeFilters.status;
      if (activeFilters.search) apiFilters.search = activeFilters.search;
      if (activeFilters.article) apiFilters.article = activeFilters.article;
      if (activeFilters.productCountry) apiFilters.productCountry = activeFilters.productCountry;
      if (activeFilters.productCity) apiFilters.productCity = activeFilters.productCity;

      // Manejar el filtro de ID de cliente
      if (activeFilters.customerId === 'none') {
        apiFilters.customerId = { isNull: true };
      } else if (activeFilters.customerId === 'any') {
        apiFilters.customerId = { isNotNull: true };
      } else if (activeFilters.customerId === 'custom' && activeFilters.customerIdValue) {
        apiFilters.customerId = activeFilters.customerIdValue;
      } else if (activeFilters.customerId) {
        apiFilters.customerId = activeFilters.customerId;
      }

      // Manejar el filtro de conversión
      if (activeFilters.hasConversion === 'yes') {
        apiFilters.hasConversion = true;
      } else if (activeFilters.hasConversion === 'no') {
        apiFilters.hasConversion = false;
      }

      // Procesar rangos de fecha solo si el tipo no es 'all'
      if (activeFilters.dateRange.type !== 'all') {
        apiFilters.dateRange = {
          startDate: activeFilters.dateRange.startDate,
          endDate: activeFilters.dateRange.endDate
        };
      }
      
      // Procesar rangos de fecha de realización
      if (activeFilters.executionDateRange.type === 'none') {
        apiFilters.executionDateRange = { isNull: true };
      } else if (activeFilters.executionDateRange.type !== 'all') {
        apiFilters.executionDateRange = {
          startDate: activeFilters.executionDateRange.startDate,
          endDate: activeFilters.executionDateRange.endDate
        };
      }

      console.log('🔍 Filtros enviados a la API:', apiFilters);
      
      const data = await saleService.getSalesWithFilters(apiFilters);
      setSales(data.sales || []);
      
      // Calcular los valores from y to basados en la página actual y el límite
      const currentPage = data.page || pagination.page;
      const currentLimit = data.limit || pagination.limit;
      const from = ((currentPage - 1) * currentLimit) + 1;
      const to = Math.min(from + currentLimit - 1, data.total || 0);
      
      // Actualizar la información de paginación
      setPagination(prev => ({
        ...prev,
        total: data.total || 0,
        pages: data.pages || 0,
        from,
        to
      }));
    } catch (err) {
      console.error('Error al cargar ventas:', err);
      setError('Error al cargar las ventas. Por favor, intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, sorting, activeFilters]);

  // Cargar ventas cuando cambie la paginación o el ordenamiento
  useEffect(() => {
    loadSales();
  }, [pagination.page, pagination.limit, sorting, loadSales]);

  // Cargar filtros desde la URL al montar el componente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newFilters = createDefaultFilters();
    const pageFromUrl = parseInt(params.get('page') || '', 10);
    const limitFromUrl = parseInt(params.get('limit') || '', 10);

    // Cargar filtros de la URL
    if (params.has('platform')) {
      newFilters.platform = params.get('platform');
    }
    if (params.has('productCountry')) {
      const country = params.get('productCountry');
      newFilters.productCountry = country;
      // Actualizar ciudades disponibles si hay un país seleccionado
      if (country && locationFilters[country]) {
        setAvailableCities(locationFilters[country]);
      }
    }
    if (params.has('productCity')) {
      newFilters.productCity = params.get('productCity');
    }
    if (params.has('status')) {
      newFilters.status = params.get('status');
    }
    if (params.has('search')) {
      newFilters.search = params.get('search');
    }
    if (params.has('article')) {
      newFilters.article = params.get('article');
    }
    if (params.has('hasConversion')) {
      newFilters.hasConversion = params.get('hasConversion');
    }
    if (params.has('customerId')) {
      const customerId = params.get('customerId');
      if (customerId === 'none' || customerId === 'any') {
        newFilters.customerId = customerId;
      } else if (customerId) {
        newFilters.customerId = 'custom';
        newFilters.customerIdValue = customerId;
      }
    }
    if (params.has('date_from')) {
      const dateFrom = params.get('date_from');
      const dateTo = params.get('date_to') || new Date().toISOString().split('T')[0];
      newFilters.dateRange = { type: 'custom', startDate: dateFrom, endDate: dateTo };
    }
    if (params.has('startDate') || params.has('endDate')) {
      newFilters.dateRange = {
        type: 'custom',
        startDate: params.get('startDate') || '',
        endDate: params.get('endDate') || ''
      };
    }
    if (params.has('executionType') && params.get('executionType') === 'none') {
      newFilters.executionDateRange = {
        type: 'none',
        startDate: '',
        endDate: ''
      };
    } else if (params.has('executionStartDate') || params.has('executionEndDate')) {
      newFilters.executionDateRange = {
        type: 'custom',
        startDate: params.get('executionStartDate') || '',
        endDate: params.get('executionEndDate') || ''
      };
    }

    // Cargar filtros de fecha
    if (params.has('dateRange')) {
      try {
        const dateRange = JSON.parse(params.get('dateRange'));
        newFilters.dateRange = dateRange;
      } catch (e) {
        console.error('Error parsing dateRange:', e);
      }
    }

    // Cargar filtros de fecha de ejecución
    if (params.has('executionDateRange')) {
      try {
        const executionDateRange = JSON.parse(params.get('executionDateRange'));
        newFilters.executionDateRange = executionDateRange;
      } catch (e) {
        console.error('Error parsing executionDateRange:', e);
      }
    }

    setFilters(newFilters);

    setPagination(prev => ({
      ...prev,
      page: Number.isInteger(pageFromUrl) && pageFromUrl > 0 ? pageFromUrl : prev.page,
      limit: Number.isInteger(limitFromUrl) && limitFromUrl > 0 ? limitFromUrl : prev.limit
    }));

    // Si hay filtros en la URL, aplicarlos automáticamente
    if (params.size > 0) {
      setActiveFilters(newFilters);
    }
    setIsUrlFiltersInitialized(true);
  }, [location.search, locationFilters]);

  // Función para calcular fechas basadas en el tipo seleccionado
  const calculateDateRange = (type) => {
    const today = new Date();
    let startDate = '';
    let endDate = '';
    
    switch (type) {
      case 'week':
        // Última semana
        startDate = new Date(today);
        startDate.setDate(today.getDate() - 7);
        startDate = startDate.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'month':
        // Último mes
        startDate = new Date(today);
        startDate.setMonth(today.getMonth() - 1);
        startDate = startDate.toISOString().split('T')[0];
        endDate = today.toISOString().split('T')[0];
        break;
      case 'custom':
      case 'all':
      default:
        // No hacemos nada, dejamos las fechas vacías
        break;
    }
    
    return { startDate, endDate };
  };

  // Manejar cambios en el tipo de rango de fecha
  const handleDateRangeTypeChange = (e) => {
    const { name, value } = e.target;
    const isExecutionDate = name === 'executionDateRange.type';
    const fieldPrefix = isExecutionDate ? 'executionDateRange' : 'dateRange';
    
    const dateRange = calculateDateRange(value);
    
    setFilters(prev => ({
      ...prev,
      [fieldPrefix]: {
        ...prev[fieldPrefix],
        type: value,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      }
    }));
  };

  // Actualizar URL automáticamente cuando cambien los filtros
  useEffect(() => {
    if (!isUrlFiltersInitialized) {
      return;
    }

    const timer = setTimeout(() => {
      updateUrlWithFilters(activeFilters, pagination);
    }, 500); // Debounce de 500ms
    
    return () => clearTimeout(timer);
  }, [activeFilters, pagination.page, pagination.limit, isUrlFiltersInitialized]);

  // Función para actualizar la URL con los filtros actuales
  const updateUrlWithFilters = (filterValues, paginationState) => {
    const url = new URL(window.location.href);

    // Limpiar parámetros existentes
    url.search = '';
    const params = new URLSearchParams();
    
    // Procesar el ID de cliente personalizado
    let customerId = filterValues.customerId;
    if (filterValues.customerId === 'custom' && filterValues.customerIdValue) {
      customerId = filterValues.customerIdValue;
    }
    
    // Añadir solo los filtros que tengan valor
    if (filterValues.productCountry) params.append('productCountry', filterValues.productCountry);
    if (filterValues.productCity) params.append('productCity', filterValues.productCity);
    if (filterValues.platform) params.append('platform', filterValues.platform);
    if (filterValues.status) params.append('status', filterValues.status);
    if (filterValues.search) params.append('search', filterValues.search);
    if (filterValues.article) params.append('article', filterValues.article);
    if (customerId) params.append('customerId', customerId);
    if (filterValues.hasConversion) params.append('hasConversion', filterValues.hasConversion);
    
    // Añadir fechas si no están vacías
    if (filterValues.dateRange?.type !== 'all') {
      if (filterValues.dateRange?.startDate) params.append('startDate', filterValues.dateRange.startDate);
      if (filterValues.dateRange?.endDate) params.append('endDate', filterValues.dateRange.endDate);
    }
    
    if (filterValues.executionDateRange?.type === 'none') {
      params.append('executionType', 'none');
    } else if (filterValues.executionDateRange?.type !== 'all') {
      if (filterValues.executionDateRange?.startDate) params.append('executionStartDate', filterValues.executionDateRange.startDate);
      if (filterValues.executionDateRange?.endDate) params.append('executionEndDate', filterValues.executionDateRange.endDate);
    }
    
    // Mantener los parámetros de paginación
    if (paginationState?.page) params.append('page', paginationState.page.toString());
    if (paginationState?.limit) params.append('limit', paginationState.limit.toString());
    
    // Actualizar la URL sin recargar la página
    url.search = params.toString();
    window.history.pushState({}, '', url);
  };

  // Limpiar todos los filtros
  const clearFilters = () => {
    const emptyFilters = createDefaultFilters();
    
    setFilters(emptyFilters);
    setActiveFilters(emptyFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    setAvailableCities([]);
    
    // Limpiar la URL
    window.history.pushState({}, '', window.location.pathname);
  };

  // Cambiar de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Cambiar el número de elementos por página
  const handleLimitChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setPagination(prev => ({ ...prev, page: 1, limit: newLimit }));
  };
  
  // Manejar orden de columnas
  const handleSort = (field) => {
    setSorting(prev => {
      if (prev.field === field) {
        // Si es el mismo campo, alternar el orden
        return { field, order: prev.order === 'ASC' ? 'DESC' : 'ASC' };
      } else {
        // Si es un campo nuevo, ordenar descendentemente por defecto
        return { field, order: 'DESC' };
      }
    });
  };
  
  // Renderizar ícono de ordenamiento
  const renderSortIcon = (field) => {
    if (sorting.field !== field) {
      return <span className="text-gray-400">⇅</span>;
    }
    return sorting.order === 'ASC' ? <span className="text-primary">↑</span> : <span className="text-primary">↓</span>;
  };

  const handleDeleteClick = async (sale) => {
    try {
      // Verificar si la venta tiene una conversión asociada
      const hasConversion = await saleService.checkSaleHasConversion(sale.id);
      setDeleteDialog({ 
        open: true, 
        sale, 
        hasConversion 
      });
    } catch (err) {
      console.error('Error al verificar la conversión:', err);
      // Si hay un error en la verificación, igualmente mostrar el diálogo normal
      setDeleteDialog({ 
        open: true, 
        sale, 
        hasConversion: false 
      });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await saleService.deleteSale(deleteDialog.sale.id);
      setDeleteDialog({ open: false, sale: null, hasConversion: false });
      loadSales();
      toast.success('Venta eliminada correctamente');
    } catch (err) {
      console.error('Error al eliminar venta:', err);
      setError('Error al eliminar la venta. Por favor, intenta nuevamente.');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, sale: null, hasConversion: false });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  // Funciones auxiliares para el estado
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved':
        return 'badge badge-success';
      case 'rejected':
        return 'badge badge-error';
      default:
        return 'badge badge-info';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved':
        return 'Aprobado';
      case 'rejected':
        return 'Rechazado';
      default:
        return 'Pendiente';
    }
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
    setError(null);
  };

  const handlePlatformChange = (event) => {
    setSelectedPlatform(event.target.value);
    setError(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!selectedFile || !selectedPlatform) {
      setError('Se requiere un archivo y una plataforma');
      return;
    }

    // Simplificamos la creación del FormData
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('platformId', selectedPlatform);
    formData.append('overwrite', event.target.elements.overwrite.value);

    setLoading(true);
    setError(null);
    setUploadResults(null);
    setUploadJob(null);
    setUploadProgress(0);

    try {
      // Iniciar la carga y obtener el jobId
      const response = await saleService.uploadBulkSales(formData);
      
      // Guardar el ID del trabajo y comenzar a consultar su estado
      setUploadJob(response);
      setIsPolling(true);
      
      // Iniciar la verificación periódica del estado del trabajo
      pollJobStatus(response.jobId);
      
      toast.success(`Procesamiento iniciado. Se le notificará cuando termine.`);
    } catch (error) {
      console.error('Error al subir ventas:', error);
      setError(error.message);
      setUploadResults(null);
      setUploadJob(null);
      setIsPolling(false);
    } finally {
      // No desactivamos el loading hasta que termine el trabajo completo
    }
  };

  // Función para consultar periódicamente el estado del trabajo
  const pollJobStatus = async (jobId) => {
    if (!jobId) return;
    
    try {
      const jobStatus = await saleService.getBulkUploadStatus(jobId);
 
      // Actualizar progreso
      setUploadProgress(jobStatus.progress || 0);
      
      // Verificar si el trabajo ha terminado
      if (jobStatus.status === 'completed') {
        setIsPolling(false);
        setLoading(false);
        
        // Obtener los errores
        let errorsArray = [];
        if (jobStatus.results && jobStatus.results.errors) {
          // Ahora el backend devuelve el array completo, no solo la cantidad
          if (Array.isArray(jobStatus.results.errors)) {
            errorsArray = jobStatus.results.errors;
          }
        }
        
        // Guardar los resultados para mostrarlos
        setUploadResults({
          created: jobStatus.results?.created || 0,
          updated: jobStatus.results?.updated || 0,
          errors: errorsArray,
          errorDetails: errorsArray
        });
        
        // Notificar al usuario
        toast.success(`Archivo procesado correctamente: ${jobStatus.results?.created || 0} ventas creadas, ${jobStatus.results?.updated || 0} actualizadas.`);
        
        // Si hay errores, mostrar notificación
        if (errorsArray.length > 0) {
          toast.error(`Se encontraron ${errorsArray.length} errores durante el procesamiento.`);
        }
        
        // Recargar la lista de ventas
        loadSales();
      } 
      else if (jobStatus.status === 'error') {
        setIsPolling(false);
        setLoading(false);
        setError(jobStatus.error || 'Error en el procesamiento');
        toast.error('Error en el procesamiento del archivo');
      }
      else {
        // Seguir consultando cada 2 segundos mientras el trabajo esté en progreso
        setTimeout(() => {
          pollJobStatus(jobId);
        }, 2000);
      }
    } catch (error) {
      console.error('Error al verificar estado del trabajo:', error);
      setIsPolling(false);
      setLoading(false);
      setError('Error al verificar el estado del procesamiento');
      toast.error('Error al verificar el estado del procesamiento');
    }
  };

  // Nuevas funciones para manejo de selección múltiple
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedSales(sales.map(sale => sale.id));
    } else {
      setSelectedSales([]);
    }
    setSelectAllPages(false);
  };

  // Función para seleccionar absolutamente todos los registros
  const handleSelectAllPages = async () => {
    try {
      setLoading(true);
      
      // Obtener IDs de todas las ventas que cumplan con los filtros actuales
      const allFilters = {
        page: 1,
        limit: 1000000, // Un número muy grande para obtener todos
        onlyIds: true // Indicador para que el backend solo retorne IDs
      };

      if (activeFilters.platform) allFilters.platform = activeFilters.platform;
      if (activeFilters.status) allFilters.status = activeFilters.status;
      if (activeFilters.search) allFilters.search = activeFilters.search;
      if (activeFilters.article) allFilters.article = activeFilters.article;
      if (activeFilters.productCountry) allFilters.productCountry = activeFilters.productCountry;
      if (activeFilters.productCity) allFilters.productCity = activeFilters.productCity;

      if (activeFilters.customerId === 'none') {
        allFilters.customerId = { isNull: true };
      } else if (activeFilters.customerId === 'any') {
        allFilters.customerId = { isNotNull: true };
      } else if (activeFilters.customerId === 'custom' && activeFilters.customerIdValue) {
        allFilters.customerId = activeFilters.customerIdValue;
      } else if (activeFilters.customerId) {
        allFilters.customerId = activeFilters.customerId;
      }

      if (activeFilters.hasConversion === 'yes') {
        allFilters.hasConversion = true;
      } else if (activeFilters.hasConversion === 'no') {
        allFilters.hasConversion = false;
      }
      
      // Procesar rangos de fecha solo si el tipo no es 'all'
      if (activeFilters.dateRange.type !== 'all') {
        allFilters.dateRange = {
          startDate: activeFilters.dateRange.startDate,
          endDate: activeFilters.dateRange.endDate
        };
      } else {
        delete allFilters.dateRange;
      }
      
      // Procesar rangos de fecha de realización
      if (activeFilters.executionDateRange.type === 'none') {
        allFilters.executionDateRange = { isNull: true };
      } else if (activeFilters.executionDateRange.type !== 'all') {
        allFilters.executionDateRange = {
          startDate: activeFilters.executionDateRange.startDate,
          endDate: activeFilters.executionDateRange.endDate
        };
      } else {
        delete allFilters.executionDateRange;
      }
      
      const allSalesIds = await saleService.getAllSalesIds(allFilters);
      
      if (selectAllPages) {
        setSelectedSales([]);
        setSelectAllPages(false);
        toast.success('Se han deseleccionado todas las ventas');
      } else {
        setSelectedSales(allSalesIds);
        setSelectAllPages(true);
        toast.success(`Se han seleccionado ${allSalesIds.length} ventas en total`);
      }
    } catch (error) {
      console.error('Error al seleccionar todas las ventas:', error);
      toast.error('No se pudieron seleccionar todas las ventas');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSale = (e, saleId) => {
    if (e.target.checked) {
      setSelectedSales([...selectedSales, saleId]);
    } else {
      setSelectedSales(selectedSales.filter(id => id !== saleId));
    }
    // Si deseleccionamos una venta, ya no tenemos todos seleccionados
    if (!e.target.checked && selectAllPages) {
      setSelectAllPages(false);
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedSales.length > 0) {
      setBulkDeleteDialog(true);
    }
  };

  const handleBulkDeleteCancel = () => {
    setBulkDeleteDialog(false);
  };

  const handleBulkStatusClick = () => {
    if (selectedSales.length > 0) {
      setBulkStatusDialog({ open: true, status: '' });
    }
  };

  const handleBulkStatusCancel = () => {
    setBulkStatusDialog({ open: false, status: '' });
  };

  const handleBulkStatusChange = (e) => {
    setBulkStatusDialog(prev => ({
      ...prev,
      status: e.target.value
    }));
  };

  const handleBulkDeleteConfirm = async () => {
    setLoading(true);
    try {
      // Crear un array de promesas para eliminar cada venta seleccionada
      // Utilizamos transacciones para asegurar que se eliminen tanto las ventas como sus conversiones
      const result = await saleService.bulkDeleteSales(selectedSales);
      
      // Actualizar la UI
      toast.success(`${result.deleted} ventas eliminadas correctamente`);
      if (result.errors > 0) {
        toast.error(`No se pudieron eliminar ${result.errors} ventas`);
      }
      
      setSelectedSales([]);
      setBulkDeleteDialog(false);
      setSelectAllPages(false);
      
      // Recargar ventas
      await loadSales();
    } catch (error) {
      console.error('Error al eliminar ventas en masa:', error);
      toast.error('Ocurrió un error al eliminar las ventas');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkStatusConfirm = async () => {
    if (!bulkStatusDialog.status) {
      toast.error('Por favor selecciona un estado');
      return;
    }

    setLoading(true);
    try {
      const result = await saleService.bulkUpdateSalesStatus(selectedSales, bulkStatusDialog.status);
      
      // Actualizar la UI
      toast.success(`${result.updated} ventas actualizadas correctamente`);
      if (result.errors > 0) {
        toast.error(`No se pudieron actualizar ${result.errors} ventas`);
      }
      
      setSelectedSales([]);
      setBulkStatusDialog({ open: false, status: '' });
      setSelectAllPages(false);
      
      // Recargar ventas
      await loadSales();
    } catch (error) {
      console.error('Error al actualizar estado de ventas en masa:', error);
      toast.error('Ocurrió un error al actualizar el estado de las ventas');
    } finally {
      setLoading(false);
    }
  };

  // Renderizado condicional para el estado de carga
  if (loading && sales.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="page-header">
        <h1 className="page-title">Gestión de Ventas</h1>
        <div className="page-actions">
          {/* Botones para operaciones con seleccionados */}
          {selectedSales.length > 0 && (
            <>
              <button
                className="btn btn-info"
                onClick={handleBulkStatusClick}
                disabled={loading}
              >
                Cambiar Estado ({selectedSales.length})
              </button>
              <button
                className="btn btn-error"
                onClick={handleBulkDeleteClick}
                disabled={loading}
              >
                Eliminar seleccionados ({selectedSales.length})
              </button>
            </>
          )}
          <button 
            onClick={() => setSelectedFile({})}
            className="btn btn-secondary"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Subir Ventas
          </button>
          <Link to="/sales/new" className="btn btn-primary">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nueva Venta
          </Link>
          <button onClick={loadSales} className="btn btn-outline">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Filtros */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title mb-4">Filtros</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Buscar</span>
              </label>
              <input
                type="text"
                name="search"
                className="input input-bordered w-full"
                placeholder="Buscar por ID de orden o producto"
                value={filters.search}
                onChange={handleFilterChange}
              />
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Plataforma</span>
              </label>
              <select
                name="platform"
                className="select select-bordered w-full"
                value={filters.platform}
                onChange={handleFilterChange}
              >
                <option value="">Todas las plataformas</option>
                {platforms.map(platform => (
                  <option key={platform.id} value={platform.id}>
                    {platform.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Estado</span>
              </label>
              <select
                name="status"
                className="select select-bordered w-full"
                value={filters.status}
                onChange={handleFilterChange}
              >
                <option value="">Todos los estados</option>
                <option value="pending">Pendiente</option>
                <option value="approved">Aprobada</option>
                <option value="rejected">Rechazada</option>
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Fecha de Venta</span>
              </label>
              <select
                name="dateRange.type"
                className="select select-bordered w-full"
                value={filters.dateRange.type}
                onChange={handleDateRangeTypeChange}
              >
                <option value="all">Todas las fechas</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
                <option value="custom">Rango personalizado</option>
              </select>
            </div>

            {filters.dateRange.type === 'custom' && (
              <>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Venta - Inicio</span>
                  </label>
                  <input
                    type="date"
                    name="dateRange.startDate"
                    className="input input-bordered w-full"
                    value={filters.dateRange.startDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Venta - Fin</span>
                  </label>
                  <input
                    type="date"
                    name="dateRange.endDate"
                    className="input input-bordered w-full"
                    value={filters.dateRange.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </>
            )}

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Fecha de Realización</span>
              </label>
              <select
                name="executionDateRange.type"
                className="select select-bordered w-full"
                value={filters.executionDateRange.type}
                onChange={handleDateRangeTypeChange}
              >
                <option value="all">Todas las fechas</option>
                <option value="week">Última semana</option>
                <option value="month">Último mes</option>
                <option value="custom">Rango personalizado</option>
                <option value="none">Sin Fecha de Realización</option>
              </select>
            </div>

            {filters.executionDateRange.type === 'custom' && (
              <>
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Realización - Inicio</span>
                  </label>
                  <input
                    type="date"
                    name="executionDateRange.startDate"
                    className="input input-bordered w-full"
                    value={filters.executionDateRange.startDate}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Fecha Realización - Fin</span>
                  </label>
                  <input
                    type="date"
                    name="executionDateRange.endDate"
                    className="input input-bordered w-full"
                    value={filters.executionDateRange.endDate}
                    onChange={handleFilterChange}
                  />
                </div>
              </>
            )}

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">País del Producto</span>
              </label>
              <select
                name="productCountry"
                className="select select-bordered w-full"
                value={filters.productCountry}
                onChange={handleCountryChange}
              >
                <option value="">Todos los países</option>
                <option value="none">No definido</option>
                {Object.keys(locationFilters).map((country) => (
                  <option key={country} value={country}>
                    {getCountryName(country)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Ciudad del Producto</span>
              </label>
              {console.log('Renderizando selector de ciudades. availableCities:', availableCities)}
              <select
                name="productCity"
                className="select select-bordered w-full"
                value={filters.productCity}
                onChange={handleFilterChange}
                disabled={!filters.productCountry || filters.productCountry === 'none'}
              >
                <option value="">Todas las ciudades</option>
                {availableCities && availableCities.length > 0 ? (
                  availableCities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No hay ciudades disponibles</option>
                )}
              </select>
              {filters.productCountry && filters.productCountry !== 'none' && availableCities && availableCities.length === 0 && (
                <div className="text-sm text-error mt-1">
                  No hay ciudades disponibles para este país
                </div>
              )}
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">ID de Cliente</span>
              </label>
              <select
                name="customerId"
                className="select select-bordered w-full"
                value={filters.customerId}
                onChange={handleFilterChange}
              >
                <option value="">Todos los clientes</option>
                <option value="none">Sin ID de cliente</option>
                <option value="any">Con ID de cliente</option>
                <option value="custom">ID específico</option>
              </select>
            </div>

            {filters.customerId === 'custom' && (
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-medium">ID de Cliente Específico</span>
                </label>
                <input
                  type="text"
                  name="customerIdValue"
                  className="input input-bordered w-full"
                  placeholder="Ingrese el ID de cliente"
                  value={filters.customerIdValue || ''}
                  onChange={(e) => setFilters(prev => ({
                    ...prev,
                    customerIdValue: e.target.value
                  }))}
                />
              </div>
            )}

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Tiene Conversión</span>
              </label>
              <select
                name="hasConversion"
                className="select select-bordered w-full"
                value={filters.hasConversion}
                onChange={handleFilterChange}
              >
                <option value="">Todas las ventas</option>
                <option value="yes">Con conversión</option>
                <option value="no">Sin conversión</option>
              </select>
            </div>

            <div className="form-control w-full">
              <label className="label">
                <span className="label-text font-medium">Artículo</span>
              </label>
              <input
                type="text"
                name="article"
                className="input input-bordered w-full"
                value={filters.article}
                onChange={handleFilterChange}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4 gap-2">
            <button 
              className="btn btn-ghost"
              onClick={clearFilters}
            >
              Limpiar Filtros
            </button>
            <button 
              className="btn btn-primary"
              onClick={applyFilters}
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      </div>


      {/* Tabla de ventas con ordenamiento */}
      <div className="overflow-x-auto">
        <div className="flex justify-between items-center mb-2">
          <button 
            className="btn btn-sm btn-outline"
            onClick={handleSelectAllPages}
            disabled={loading}
          >
            Seleccionar todas las ventas {selectAllPages ? '(' + selectedSales.length + ')' : ''}
          </button>
        </div>
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th className="w-10">
                <input 
                  type="checkbox" 
                  className="checkbox" 
                  checked={selectedSales.length > 0 && selectedSales.length === sales.length}
                  onChange={handleSelectAll}
                />
              </th>
              <th onClick={() => handleSort('saleDate')} className="cursor-pointer">
                Fecha Venta {renderSortIcon('saleDate')}
              </th>
              <th onClick={() => handleSort('executionDate')} className="cursor-pointer">
                Fecha Realización {renderSortIcon('executionDate')}
              </th>
              <th onClick={() => handleSort('platformId')} className="cursor-pointer">
                Plataforma {renderSortIcon('platformId')}
              </th>
              <th onClick={() => handleSort('orderId')} className="cursor-pointer">
                ID Orden {renderSortIcon('orderId')}
              </th>
              <th onClick={() => handleSort('productName')} className="cursor-pointer">
                Producto {renderSortIcon('productName')}
              </th>
              <th>Artículo</th>
              <th>ID Cliente</th>
              <th onClick={() => handleSort('amount')} className="cursor-pointer">
                Monto {renderSortIcon('amount')}
              </th>
              <th onClick={() => handleSort('commission')} className="cursor-pointer">
                Comisión {renderSortIcon('commission')}
              </th>
              <th onClick={() => handleSort('status')} className="cursor-pointer">
                Estado {renderSortIcon('status')}
              </th>
              <th>
                Google Ads
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {sales.length > 0 ? (
              sales.map(sale => (
                <tr key={sale.id}>
                  <td>
                    <input 
                      type="checkbox" 
                      className="checkbox" 
                      checked={selectedSales.includes(sale.id)}
                      onChange={(e) => handleSelectSale(e, sale.id)}
                    />
                  </td>
                  <td>{formatDate(sale.saleDate)}</td>
                  <td>{formatDate(sale.executionDate)}</td>
                  <td>
                    <span 
                      className="badge badge-outline"
                      style={{ 
                        '--badge-color': sale.Platform?.color || '#808080'
                      }}
                    >
                      {sale.Platform?.name || '-'}
                    </span>
                  </td>
                  <td>{sale.orderId}</td>
                  <td>
                    {sale.Product?.id ? (
                      <Link to={`/products/${sale.Product.id}`} className="link link-hover">
                        {sale.Product.name}
                      </Link>
                    ) : (
                      sale.Product?.name || '-'
                    )}
                  </td>
                  <td>
                    {sale.articleId ? (
                      <Link to={`/articles/${sale.articleId}`} className="link link-hover">
                        {sale.article || `Artículo #${sale.articleId}`}
                      </Link>
                    ) : (
                      sale.article || '-'
                    )}
                  </td>
                  <td>
                    {sale.customerId ? (
                      <Link to={`https://cometeelmundo.activehosted.com/app/contacts/${sale.customerId}`} target="_blank" className="link link-primary">
                        {sale.customerId}
                      </Link>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td>{formatCurrency(sale.amountEUR)}</td>
                  <td>{formatCurrency(sale.commissionEUR)}</td>
                  <td>
                    <span className={getStatusBadgeClass(sale.status)}>
                      {getStatusText(sale.status)}
                    </span>
                  </td>
                  <td>
                    {sale.GoogleAdsConversion ? (
                      <Link to={`/conversions/${sale.GoogleAdsConversion.id}`} className="link link-primary">
                        Conversion
                      </Link>
                    ) : (
                      <span className="badge badge-ghost">-</span>
                    )}
                  </td>
                  <td>
                    <div className="table-actions">
                      <Link to={`/sales/${sale.id}`} className="table-action-btn table-action-btn-info" title="Ver venta">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </Link>
                      <Link to={`/sales/edit/${sale.id}`} className="table-action-btn table-action-btn-neutral" title="Editar venta">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </Link>
                      <button onClick={() => handleDeleteClick(sale)} className="table-action-btn table-action-btn-danger" title="Eliminar venta">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="text-center py-4">
                  No se encontraron resultados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Paginación */}
      <div className="flex flex-col md:flex-row items-center justify-between mt-4 gap-2">
        <div className="flex items-center">
          <span className="mr-2">Mostrar:</span>
          <select
            value={pagination.limit}
            onChange={handleLimitChange}
            className="select select-bordered select-sm"
          >
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        
        <div className="pagination join">
          <button
            onClick={() => handlePageChange(1)}
            disabled={pagination.page === 1}
            className="join-item btn btn-sm"
          >
            «
          </button>
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
            className="join-item btn btn-sm"
          >
            ‹
          </button>
          
          {paginationButtons.map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`join-item btn btn-sm ${pagination.page === page ? 'btn-active' : ''}`}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.pages}
            className="join-item btn btn-sm"
          >
            ›
          </button>
          <button
            onClick={() => handlePageChange(pagination.pages)}
            disabled={pagination.page === pagination.pages}
            className="join-item btn btn-sm"
          >
            »
          </button>
        </div>
        
        <div className="text-sm">
          Mostrando {pagination.from}-{pagination.to} de {pagination.total} ventas
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      {deleteDialog.open && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmar Eliminación</h3>
            {deleteDialog.hasConversion ? (
              <div className="py-4">
                <div className="alert alert-warning mb-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>¡Atención! Esta venta tiene una conversión de Google Ads asociada.</span>
                </div>
                <p>
                  Si eliminas esta venta, también se eliminará la conversión asociada. 
                  Esta acción no se puede deshacer.
                </p>
              </div>
            ) : (
              <p className="py-4">
                ¿Estás seguro de que deseas eliminar esta venta? Esta acción no se puede deshacer.
              </p>
            )}
            <div className="modal-action">
              <button onClick={handleDeleteCancel} className="btn">
                Cancelar
              </button>
              <button onClick={handleDeleteConfirm} className="btn btn-error">
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para subir archivos CSV */}
      {selectedFile && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Subir Ventas desde CSV</h3>
            
            {error && (
              <div className="alert alert-error mt-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}
            
            {/* Mostrar progreso durante el procesamiento */}
            {isPolling && (
              <div className="my-4">
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Procesando archivo...</span>
                  <span className="text-sm font-medium">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <p className="text-sm mt-2">Por favor, espere mientras se procesa el archivo. Esta operación puede tardar varios minutos dependiendo del tamaño del archivo.</p>
              </div>
            )}
            
            {/* Mostrar resultados cuando termina el procesamiento o hay errores */}
            {uploadResults ? (
              <div className="my-4">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-green-500">{uploadResults.created}</div>
                    <div className="text-sm">Ventas creadas</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-cyan-500">{uploadResults.updated}</div>
                    <div className="text-sm">Ventas actualizadas</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-red-500">{uploadResults.errors.length}</div>
                    <div className="text-sm">Errores</div>
                  </div>
                </div>
                {/* Mostrar detalles de errores si existen */}
                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <div className="mt-6">
                    <h4 className="font-semibold mb-2 text-red-600">Errores detectados:</h4>
                    <div className="max-h-56 overflow-y-auto rounded-lg border border-red-200 bg-red-50">
                      <table className="table-auto w-full text-sm">
                        <thead className="bg-red-100 text-red-800 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left font-medium">Detalle del error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-200">
                          {uploadResults.errors.map((err, idx) => (
                            <tr key={idx} className="text-red-700 hover:bg-red-100">
                              <td className="px-4 py-3">
                                {err.error}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                <div className="modal-action">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => {
                      setSelectedFile(null);
                      setUploadJob(null);
                      setUploadResults(null);
                      setError(null);
                      setIsPolling(false);
                    }}
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Plataforma</span>
                  </label>
                  <select
                    className="select select-bordered w-full"
                    value={selectedPlatform}
                    onChange={handlePlatformChange}
                    disabled={loading}
                  >
                    <option value="">Seleccionar plataforma</option>
                    {platforms.map((platform) => (
                      <option key={platform.id} value={platform.id}>
                        {platform.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-control w-full">
                  <label className="label">
                    <span className="label-text font-medium">Archivo CSV</span>
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    className="file-input file-input-bordered w-full"
                    onChange={handleFileChange}
                    disabled={loading}
                  />
                  <label className="label">
                    <span className="label-text-alt">Solo se aceptan archivos CSV</span>
                  </label>
                </div>

                {/* Campo oculto para sobrescribir ventas por defecto */}
                <input type="hidden" name="overwrite" value="true" />

                <div className="modal-action">
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setSelectedFile(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !selectedFile || !selectedPlatform}
                  >
                    {loading ? (
                      <span className="loading loading-spinner loading-md"></span>
                    ) : (
                      'Subir'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación masiva */}
      {bulkDeleteDialog && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Confirmar Eliminación Masiva</h3>
            <div className="py-4">
              <div className="alert alert-warning mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <span>¡Atención! Vas a eliminar {selectedSales.length} ventas.</span>
              </div>
              <p>
                Esta acción eliminará todas las ventas seleccionadas y sus conversiones asociadas.
                Esta acción no se puede deshacer.
              </p>
              {selectAllPages && (
                <div className="alert alert-error mt-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Has seleccionado todas las ventas que cumplen con los filtros actuales ({selectedSales.length} en total). Esta operación puede tardar varios minutos.</span>
                </div>
              )}
            </div>
            <div className="modal-action">
              <button onClick={handleBulkDeleteCancel} className="btn">
                Cancelar
              </button>
              <button 
                onClick={handleBulkDeleteConfirm} 
                className="btn btn-error" 
                disabled={loading}
              >
                {loading ? 
                  <span className="loading loading-spinner loading-sm"></span> : 
                  'Eliminar'
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para cambio de estado masivo */}
      {bulkStatusDialog.open && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">Cambiar Estado de Ventas</h3>
            <div className="py-4">
              <div className="alert alert-info mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>Vas a cambiar el estado de {selectedSales.length} ventas seleccionadas.</span>
              </div>
              
              <div className="form-control w-full">
                <label className="label">
                  <span className="label-text font-medium">Nuevo estado</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={bulkStatusDialog.status}
                  onChange={handleBulkStatusChange}
                >
                  <option value="">Seleccionar estado</option>
                  <option value="pending">Pendiente</option>
                  <option value="approved">Aprobada</option>
                  <option value="rejected">Rechazada</option>
                </select>
              </div>
              
              {selectAllPages && (
                <div className="alert alert-warning mt-4">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span>Has seleccionado todas las ventas que cumplen con los filtros actuales ({selectedSales.length} en total). Esta operación puede tardar varios minutos.</span>
                </div>
              )}
            </div>
            <div className="modal-action">
              <button onClick={handleBulkStatusCancel} className="btn">
                Cancelar
              </button>
              <button 
                onClick={handleBulkStatusConfirm} 
                className="btn btn-primary" 
                disabled={loading || !bulkStatusDialog.status}
              >
                {loading ? 
                  <span className="loading loading-spinner loading-sm"></span> : 
                  'Cambiar Estado'
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesScreen; 
